
/**
 * Aggressive mobile performance optimizations for LCP improvement
 */

// Preconnect to critical origins immediately
export function addCriticalResourceHints() {
  if (typeof document === 'undefined') return;
  
  const criticalOrigins = [
    'https://images.unsplash.com',
    'https://fonts.gstatic.com',
    'https://eyedvzmodfhyrxnhhrvf.supabase.co'
  ];
  
  criticalOrigins.forEach(origin => {
    if (!document.querySelector(`link[rel="preconnect"][href="${origin}"]`)) {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = origin;
      link.crossOrigin = 'anonymous';
      document.head.insertBefore(link, document.head.firstChild);
    }
  });
}

// Aggressive mobile image optimization
export function getMobileOptimizedImageUrl(url: string, extraAggressive: boolean = false): string {
  if (!url) return url;
  
  try {
    const urlObj = new URL(url);
    
    if (urlObj.hostname.includes('unsplash.com')) {
      // Very aggressive sizing for mobile
      const width = extraAggressive ? 320 : (window.innerWidth <= 480 ? 480 : 640);
      const height = extraAggressive ? 240 : (window.innerWidth <= 480 ? 360 : 480);
      const quality = extraAggressive ? 50 : (window.innerWidth <= 480 ? 60 : 70);
      
      urlObj.searchParams.set('w', width.toString());
      urlObj.searchParams.set('h', height.toString());
      urlObj.searchParams.set('q', quality.toString());
      urlObj.searchParams.set('auto', 'format,compress');
      urlObj.searchParams.set('fit', 'crop');
      urlObj.searchParams.set('fm', 'webp');
      
      // Add device pixel ratio for mobile
      if (window.devicePixelRatio && window.devicePixelRatio > 1) {
        urlObj.searchParams.set('dpr', Math.min(window.devicePixelRatio, 2).toString());
      }
    }
    
    if (urlObj.hostname.includes('supabase.co')) {
      const width = extraAggressive ? '320' : '480';
      const height = extraAggressive ? '240' : '360';
      const quality = extraAggressive ? '50' : '65';
      
      urlObj.searchParams.set('width', width);
      urlObj.searchParams.set('height', height);
      urlObj.searchParams.set('quality', quality);
      urlObj.searchParams.set('format', 'webp');
      urlObj.searchParams.set('resize', 'cover');
    }
    
    return urlObj.toString();
  } catch (e) {
    return url;
  }
}

// Critical mobile CSS for immediate injection
export function injectCriticalMobileCSS() {
  const criticalCSS = `
    /* Ultra-critical mobile CSS */
    @media (max-width: 768px) {
      * {
        box-sizing: border-box;
        transition-duration: 0.1s !important;
        animation-duration: 0.1s !important;
      }
      
      body {
        margin: 0;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        text-rendering: optimizeSpeed;
        -webkit-font-smoothing: subpixel-antialiased;
        font-smooth: never;
      }
      
      /* Destination hero critical styles */
      .destination-hero {
        width: 100%;
        height: 240px !important;
        position: relative;
        overflow: hidden;
        border-radius: 8px;
        background: linear-gradient(45deg, #60a5fa, #6366f1);
        contain: layout style paint;
        will-change: contents;
      }
      
      .destination-hero img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
      }
      
      /* Container optimization */
      .container {
        padding-left: 8px !important;
        padding-right: 8px !important;
        max-width: 100% !important;
      }
      
      /* Reduce shadow complexity */
      .shadow-lg, .shadow-xl, .shadow-2xl {
        box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
      }
      
      /* Optimize buttons for touch */
      button, .btn, a {
        min-height: 44px;
        min-width: 44px;
      }
      
      /* Content visibility optimizations */
      .below-fold {
        content-visibility: auto;
        contain-intrinsic-size: 1px 200px;
      }
      
      /* Disable expensive transforms */
      .animate-pulse {
        animation: none !important;
      }
    }
  `;
  
  const style = document.createElement('style');
  style.textContent = criticalCSS;
  document.head.insertBefore(style, document.head.firstChild);
}

// Initialize all mobile optimizations
export function initMobileOptimizations() {
  if (typeof window === 'undefined') return;
  
  console.log('🚀 Initializing aggressive mobile optimizations');
  
  // Add resource hints first
  addCriticalResourceHints();
  
  // Inject critical CSS immediately
  injectCriticalMobileCSS();
  
  // Mobile-specific optimizations
  if (window.innerWidth <= 768) {
    document.documentElement.classList.add('mobile-device');
    
    // Optimize text rendering with proper type assertion
    const bodyStyle = document.body.style as any;
    bodyStyle.textRendering = 'optimizeSpeed';
    bodyStyle.webkitFontSmoothing = 'subpixel-antialiased';
    
    // Reduce main thread work
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => {
        // Defer non-critical images
        document.querySelectorAll('img:not([data-lcp="true"])').forEach(img => {
          (img as HTMLImageElement).loading = 'lazy';
          (img as any).fetchPriority = 'low';
        });
        
        // Apply content visibility to non-critical sections
        document.querySelectorAll('.below-fold, .non-critical').forEach(el => {
          (el as HTMLElement).style.contentVisibility = 'auto';
          (el as HTMLElement).style.containIntrinsicSize = '1px 150px';
        });
      });
    }
    
    // Optimize viewport
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=5, viewport-fit=cover');
    }
    
    console.log('📱 Mobile optimizations applied');
  }
}
